import {
  QueryClient,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { CachedQueryKeys } from "./cachedQueryKeys";
import {
  getActiveCarts,
  AllUserRetailerShoppingCartsResponse,
  updateItemsToUserRetailerShoppingCart,
  removeItemFromShoppingCart,
} from "../rest/checkout";
import { sumAllItemQuantities, sumItemQuantities } from "@/utils";
import { BCQueryOptions } from "./types";
import BagButton from "@/components/button/BagButton.tsx";
import { ShoppingCartItem, UserRetailerShoppingCart } from "@/type";
import _cloneDeep from "lodash/cloneDeep";
import { validateCartItemLimits } from "@/utils/cartValidation";

/**
 * Updates the cart data **in-place**
 *
 * @param carts - The array of carts to update
 * @param retailerId - The retailer ID of the cart to update
 * @param items - The items to update the cart with
 * @param action - The action to perform on the cart
 */
const mutateLocalCarts = (
  carts: UserRetailerShoppingCart[] = [],
  retailerId: number,
  items: ShoppingCartItem[],
  action: "add" | "remove"
) => {
  // Find the cart for this retailer
  const cartIndex = carts.findIndex((cart) => cart.retailerId === retailerId);

  // If the cart is NOT found and we're NOT adding a new one, there's nothing to do
  if (cartIndex === -1 && action !== "add") {
    return;
  }
  // Handle the case where we're adding to a non-existent cart
  if (cartIndex === -1) {
    const userId = carts[0]?.userId || "";
    // create a new cart with the items
    carts.push({
      retailerId,
      items: [...items], // Clone the items array
      totalItems: sumItemQuantities(items),
      status: "active",
      createdTime: new Date(),
      updatedTime: new Date(),
      userId,
      shoppingCartId: "",
      createdBy: "",
      updatedBy: "",
    });
    // return early because we don't need to update the cart
    return;
  }

  // Update the existing cart
  const cart = carts[cartIndex];
  const updatedItems = updateCartItems(cart.items, items, action);

  // If all items were removed, remove the cart entirely
  if (updatedItems.length === 0) {
    carts.splice(cartIndex, 1);
    return;
  }

  // Update the cart with new items and recalculate totals
  cart.items = updatedItems;
};

/**
 * Updates cart items based on the action
 * Returns a new array of items
 */
const updateCartItems = (
  existingItems: ShoppingCartItem[] = [],
  newItems: ShoppingCartItem[],
  action: "add" | "remove"
): ShoppingCartItem[] => {
  if (action === "remove") {
    return existingItems.filter(
      (item) =>
        !newItems.some((newItem) => newItem.retailerSku === item.retailerSku)
    );
  }
  // Create a map of existing items for easier updates
  const itemMap = new Map();
  existingItems.forEach((item) => itemMap.set(item.retailerSku, { ...item }));

  // Process each new item
  for (const newItem of newItems) {
    const existingItem = itemMap.get(newItem.retailerSku);

    if (existingItem) {
      // Update existing item
      existingItem.quantity = Math.max(
        0,
        existingItem.quantity + newItem.quantity
      ); // Remove item if quantity becomes zero
      if (existingItem.quantity === 0) {
        itemMap.delete(newItem.retailerSku);
        continue;
      }
    } else if (action === "add") {
      // Add new item (only for 'add' action)
      itemMap.set(newItem.retailerSku, { ...newItem });
    }
  }

  // Convert the map back to an array
  return Array.from(itemMap.values());
};

// Define a type for cart operation parameters
interface CartOperationParams {
  action: "add" | "remove";
  retailerId: number;
  items: ShoppingCartItem[];
}

const handleCartOperation = async (
  params: CartOperationParams,
  queryClient: QueryClient
) => {
  const { action, retailerId, items } = params;
  if (action === "remove") {
    const response = await removeItemFromShoppingCart({
      retailerId,
      items: items,
    });
    return response;
  }
  if (action !== "add") {
    throw new Error("Invalid action");
  }

  const optimisticallyUpdatedCarts = queryClient.getQueryData(
    activeCartsQueryOptions().queryKey
  );
  const updatedRetailerCart =
    optimisticallyUpdatedCarts?.userRetailerShoppingCarts?.find(
      (cart) => cart.retailerId === retailerId
    );

  if (!optimisticallyUpdatedCarts || !updatedRetailerCart) {
    // there is no cart in the cache, just call the API
    BagButton?.AnimateAddProductToBag(items[0].stacklineSku);
    const response = await updateItemsToUserRetailerShoppingCart({
      retailerId,
      items,
    });
    return response;
  }

  const requestedItemsSkuSet = new Set(items.map((item) => item.retailerSku));
  const totalItems = optimisticallyUpdatedCarts
    ? sumAllItemQuantities(optimisticallyUpdatedCarts.userRetailerShoppingCarts)
    : 0;
  // Validate each item in the cart being updated
  updatedRetailerCart?.items.forEach((item) => {
    // throw an error if the item quantity is invalid
    validateCartItemLimits(item.quantity, totalItems);
  });
  // filter the items to only include the requested items
  const updatedItems = updatedRetailerCart.items.filter((item) =>
    requestedItemsSkuSet.has(item.retailerSku)
  );

  // no error thrown means items are valid,
  BagButton?.AnimateAddProductToBag(items[0].stacklineSku);
  const response = await updateItemsToUserRetailerShoppingCart({
    retailerId,
    items: updatedItems,
  });
  return response;
};

export const activeCartsQueryOptions = () => {
  return queryOptions({
    queryKey: [CachedQueryKeys.UserCart],
    queryFn: ({ signal }) => getActiveCarts(signal),
    staleTime: 0,
    refetchOnWindowFocus: "always",
  });
};

export const useAllCarts = <TSelect = AllUserRetailerShoppingCartsResponse>(
  options?: BCQueryOptions<
    typeof activeCartsQueryOptions,
    AllUserRetailerShoppingCartsResponse,
    TSelect
  >
) => {
  return useQuery({
    ...activeCartsQueryOptions(),
    ...options,
    select: options?.select,
  });
};

export const useCartCount = () => {
  return useQuery({
    ...activeCartsQueryOptions(),
    select: (data) => sumAllItemQuantities(data.userRetailerShoppingCarts),
    // ensures we don't trigger any fetches, only read from cache and re-render when the cache is updated
    enabled: false,
    staleTime: Infinity,
  });
};

/**
 * Hook for optimistically updating shopping cart items
 * Returns a mutation function that can be used to add/remove items from cart
 */
export const useCartUpdates = () => {
  const queryClient = useQueryClient();
  const cartQueryKey = activeCartsQueryOptions().queryKey;

  return useMutation({
    mutationFn: (params: CartOperationParams) =>
      handleCartOperation(params, queryClient),

    onMutate: async ({ retailerId, items, action }: CartOperationParams) => {
      // this is called first, we handle optimistic updates here
      // Cancel any outgoing refetches to avoid conflicting updates
      await queryClient.cancelQueries({ queryKey: cartQueryKey });

      // Save current data for potential rollback
      const previousData = queryClient.getQueryData(cartQueryKey);
      if (!previousData) {
        return { previousData: null };
      }

      try {
        // Create a deep copy of the carts to avoid mutating the cache directly
        const localCarts = _cloneDeep(
          previousData?.userRetailerShoppingCarts ?? []
        );

        // Process the cart update
        mutateLocalCarts(localCarts, retailerId, items, action);

        // Apply the optimistic update to the query data
        queryClient.setQueryData<AllUserRetailerShoppingCartsResponse>(
          cartQueryKey,
          {
            ...previousData,
            userRetailerShoppingCarts: localCarts,
          }
        );

        return { previousData };
      } catch (error) {
        console.error("Error in optimistic update:", error);
        return { previousData };
      }
    },

    onError: (_error, _params, context) => {
      // Roll back to the previous value on error
      if (context?.previousData) {
        queryClient.setQueryData(cartQueryKey, context.previousData);
      }
    },

    onSettled: () => {
      // Refetch fresh data after mutation completes (success or error)
      queryClient.invalidateQueries({
        queryKey: cartQueryKey,
        refetchType: "none",
      });
    },
  });
};
